import { zodResolver } from "@hookform/resolvers/zod";
import { Deferred, router, usePage } from "@inertiajs/react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { format, formatDistanceToNow } from "date-fns";
import {
	Activity,
	AtSignIcon,
	BookHeart,
	BriefcaseMedical,
	Calendar,
	CalendarDays,
	CalendarPlus,
	ClipboardList,
	Dot,
	Eye,
	EyeClosed,
	Fingerprint,
	Group,
	Hospital,
	IdCard,
	InfinityIcon,
	LoaderIcon,
	Mail,
	Microscope,
	Phone,
	Stethoscope,
	Users,
} from "lucide-react";
import {
	type ComponentProps,
	Fragment,
	Suspense,
	useMemo,
	useState,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { toast } from "sonner";
import { z } from "zod";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import {
	DetailsAddressSection,
	useDetailsTimeline,
} from "@/components/admin-portal/therapist/relocation/details-address-section";
import { useDateContext } from "@/components/providers/date-provider";
import ApptCard from "@/components/shared/appt-card";
import { LoadingBasic } from "@/components/shared/loading";
import { ResponsiveDialogButton } from "@/components/shared/responsive-dialog";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { getGenderIcon } from "@/hooks/use-gender";
import { getCsrfToken } from "@/lib/csrf";
import i18n from "@/lib/i18n";
import { getEmpStatusBadgeVariant } from "@/lib/therapists";
import { useTherapistEnumLabel } from "@/lib/therapists/enum-label";
import { cn, generateInitials } from "@/lib/utils";
import { PASSWORD_WITH_CONFIRMATION_SCHEMA } from "@/lib/validation";
import type { TherapistIndexGlobalPageProps } from "@/pages/AdminPortal/Therapist/Index";
import type { Therapist } from "@/types/admin-portal/therapist";
import type { GlobalPageProps, ResponsiveDialogMode } from "@/types/globals";

/* change password feature */
export interface ChangePasswordContentProps extends ComponentProps<"div"> {
	selectedTherapistAccount: Therapist["user"];
	handleOpenChange?: (value: boolean) => void;
	forceMode?: ResponsiveDialogMode;
}

export const ChangePasswordContent = ({
	className,
	selectedTherapistAccount,
	forceMode,
}: ChangePasswordContentProps) => {
	const { props: globalProps } = usePage<GlobalPageProps>();
	const { t } = useTranslation("therapists");
	// A reset link would land the therapist on this portal's sign-in page, which refuses
	// them now; setting the password here is the way an admin helps them.
	const [isLoading, setIsLoading] = useState({ form: false });

	// for form change password
	const buttonProps = useMemo<ResponsiveDialogButton>(() => {
		return { isLoading: isLoading.form, forceMode };
	}, [isLoading, forceMode]);
	const [passwordVisibility, setPasswordVisibility] = useState({
		new: false,
		confirmation: false,
	});
	const form = useForm<z.infer<typeof PASSWORD_WITH_CONFIRMATION_SCHEMA>>({
		resolver: zodResolver(PASSWORD_WITH_CONFIRMATION_SCHEMA),
		defaultValues: {
			password: "",
			passwordConfirmation: "",
		},
		mode: "onBlur",
	});
	function onSubmit(values: z.infer<typeof PASSWORD_WITH_CONFIRMATION_SCHEMA>) {
		const email = selectedTherapistAccount.email;
		console.log(`Submitting form to change password for ${email} ...`);
		router.put(
			globalProps.adminPortal.router.adminPortal.therapistManagement
				.changePassword,
			deepTransformKeysToSnakeCase({
				user: { ...values, email },
			}),
			{
				preserveScroll: true,
				preserveState: true,
				onStart: () => {
					setIsLoading({ ...isLoading, form: true });
				},
				onFinish: () => {
					setTimeout(() => {
						setIsLoading({ ...isLoading, form: false });
					}, 250);
				},
			},
		);
		console.log("Password successfully changed...");
	}

	return (
		<div className={cn("grid gap-4", className)}>
			<div className="grid w-full max-w-sm items-center gap-1.5">
				<Label htmlFor="email">{t("change_password.email")}</Label>
				<Input
					type="email"
					id="email"
					placeholder={t("change_password.email_placeholder")}
					readOnly
					value={selectedTherapistAccount.email}
				/>
			</div>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("change_password.new_password")}</FormLabel>
								<FormControl>
									<div className="relative">
										<Input
											{...field}
											type={passwordVisibility.new ? "text" : "password"}
											placeholder={t(
												"change_password.new_password_placeholder",
											)}
											autoComplete="new-password"
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="absolute -translate-y-1/2 right-1 top-1/2 h-7 w-7 text-muted-foreground"
											onClick={() => {
												setPasswordVisibility({
													...passwordVisibility,
													new: !passwordVisibility.new,
												});
											}}
										>
											{!passwordVisibility.new ? (
												<Eye className="size-4" />
											) : (
												<EyeClosed className="size-4" />
											)}
											<span className="sr-only">
												{t("change_password.toggle_visibility")}
											</span>
										</Button>
									</div>
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="passwordConfirmation"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("change_password.password_confirmation")}
								</FormLabel>
								<FormControl>
									<div className="relative">
										<Input
											{...field}
											type={
												passwordVisibility.confirmation ? "text" : "password"
											}
											placeholder={t(
												"change_password.password_confirmation_placeholder",
											)}
											autoComplete="new-password"
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="absolute -translate-y-1/2 right-1 top-1/2 h-7 w-7 text-muted-foreground"
											onClick={() => {
												setPasswordVisibility({
													...passwordVisibility,
													confirmation: !passwordVisibility.confirmation,
												});
											}}
										>
											{!passwordVisibility.confirmation ? (
												<Eye className="size-4" />
											) : (
												<EyeClosed className="size-4" />
											)}
											<span className="sr-only">
												{t("change_password.toggle_visibility")}
											</span>
										</Button>
									</div>
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>

					<ResponsiveDialogButton {...buttonProps} />
				</form>
			</Form>
		</div>
	);
};

// for delete the therapist
export interface DeleteTherapistAlertProps extends ComponentProps<"dialog"> {
	title: string;
	description: string;
	isOpen: boolean;
	onOpenChange?: (open: boolean) => void;
	selectedTherapist: Therapist;
}

export function DeleteTherapistAlert({
	title,
	description,
	isOpen,
	onOpenChange,
	selectedTherapist,
}: DeleteTherapistAlertProps) {
	const { props: globalProps } = usePage<GlobalPageProps>();
	const { t } = useTranslation("therapists");
	const [isLoading, setIsLoading] = useState(false);
	const formSchema = z.object({ id: z.string() });
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			id: selectedTherapist.id,
		},
		mode: "onSubmit",
	});
	const onSubmit = (values: z.infer<typeof formSchema>) => {
		console.log("Deleting the therapist...");

		const routeURL = `${globalProps.adminPortal.router.adminPortal.therapistManagement.index}/${values.id}`;
		router.delete(routeURL, {
			preserveScroll: true,
			preserveState: true,
			onStart: () => {
				setIsLoading(true);
			},
			onFinish: () => {
				setTimeout(() => {
					setIsLoading(false);
				}, 250);
			},
		});

		console.log("Finished process to delete the therapist...");
	};

	return (
		<AlertDialog open={isOpen} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>{description}</AlertDialogDescription>
				</AlertDialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<AlertDialogFooter>
							<AlertDialogCancel>{t("tags.cancel")}</AlertDialogCancel>
							<Button type="submit" variant="destructive" disabled={isLoading}>
								{isLoading ? (
									<>
										<LoaderIcon className="animate-spin" />
										<span>{t("dialog.delete.loading")}</span>
									</>
								) : (
									<span>{t("dialog.delete.confirm")}</span>
								)}
							</Button>
						</AlertDialogFooter>
					</form>
				</Form>
			</AlertDialogContent>
		</AlertDialog>
	);
}

function NikValue({
	therapistId,
	masked,
}: {
	therapistId: string;
	masked?: string | null;
}) {
	const { props: globalProps } = usePage<GlobalPageProps>();
	const { t } = useTranslation("therapists");
	const [revealed, setRevealed] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	if (!masked) return <>{t("list.not_available")}</>;

	const reveal = async () => {
		setIsLoading(true);
		try {
			const response = await fetch(
				`${globalProps.adminPortal.router.adminPortal.therapistManagement.index}/${therapistId}/reveal-nik`,
				{
					method: "POST",
					headers: {
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
				},
			);
			const data = (await response.json().catch(() => ({}))) as {
				nik?: string;
				error?: string;
			};
			if (!response.ok || !data.nik) throw new Error(data.error);

			setRevealed(data.nik);
		} catch (error) {
			toast.error(t("details.nik.reveal_error"), {
				description: error instanceof Error ? error.message : undefined,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<span className="inline-flex items-center gap-2">
			<span className="font-mono">{revealed ?? masked}</span>
			<Button
				type="button"
				size="xs"
				variant="link"
				className="h-auto p-0"
				disabled={isLoading}
				onClick={revealed ? () => setRevealed(null) : reveal}
			>
				{isLoading ? (
					<LoaderIcon className="animate-spin" />
				) : revealed ? (
					t("details.nik.hide")
				) : (
					t("details.nik.reveal")
				)}
			</Button>
		</span>
	);
}

// for details the therapist
interface DetailsTherapistContentProps extends ComponentProps<"div"> {
	therapist: NonNullable<TherapistIndexGlobalPageProps["selectedTherapist"]>;
	appts: TherapistIndexGlobalPageProps["selectedTherapistAppts"];
	timeline: TherapistIndexGlobalPageProps["selectedTherapistTimeline"];
	onPageChange: (page: number, limit?: number) => void;
}
export function DetailsTherapistContent({
	className,
	therapist,
	appts,
	timeline,
	onPageChange,
}: DetailsTherapistContentProps) {
	const { props: globalProps } = usePage<GlobalPageProps>();
	const { t } = useTranslation("therapists");
	const enumLabel = useTherapistEnumLabel();
	const isDekstop = useMediaQuery("(min-width: 768px)");
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const addressTimeline = useDetailsTimeline(therapist.id, timeline);

	// account details
	const { isAuthCurrentUser, isAuthSuperAdmin } = useAuth({
		user: therapist.user,
		auth: globalProps.auth,
	});
	const isOnline = therapist.user["isOnline?"];
	const currentIP = therapist.user.currentSignInIp;
	const lastIP = therapist.user.lastSignInIp;
	const lastOnlineAt = therapist.user.lastOnlineAt;
	const isSuspended = therapist.user["suspended?"];
	const suspendedAt = therapist.user.suspendAt;
	const suspendEnd = therapist.user.suspendEnd;
	const accounts = useMemo(() => {
		type itemID =
			| "lastOnlineAt"
			| "lastSignInAt"
			| "currentSignInIp"
			| "lastSignInIp"
			| "suspendedAt"
			| "suspendEnd";
		const items = [
			{
				id: "lastOnlineAt" as itemID,
				title: t("details.field.last_online_session"),
				value: therapist?.user?.lastOnlineAt
					? formatDistanceToNow(therapist?.user?.lastOnlineAt, {
							includeSeconds: true,
							addSuffix: true,
						})
					: "-",
			},
			{
				id: "lastSignInAt" as itemID,
				title: t("details.field.last_sign_in"),
				value: therapist?.user?.lastSignInAt
					? formatDistanceToNow(therapist?.user?.lastSignInAt, {
							includeSeconds: true,
							addSuffix: true,
						})
					: "-",
			},
		];

		items.push({
			id: "currentSignInIp" as itemID,
			title: t("details.field.current_ip"),
			value: therapist?.user?.currentSignInIp || "-",
		});
		items.push({
			id: "lastSignInIp" as itemID,
			title: t("details.field.last_ip"),
			value: therapist?.user?.lastSignInIp || "-",
		});

		if (isSuspended) {
			items.push({
				id: "suspendedAt" as itemID,
				title: t("details.field.suspend_on"),
				value: suspendedAt ? format(suspendedAt, "PP") : "-",
			});
			items.push({
				id: "suspendEnd" as itemID,
				title: t("details.field.suspend_until"),
				value: suspendEnd ? format(suspendEnd, "PP") : "",
			});
		}

		return {
			title: t("details.section.account_activity"),
			icon: Fingerprint,
			items,
		};
	}, [therapist?.user, isSuspended, suspendedAt, suspendEnd, t]);

	// profile details
	const personalDetails = useMemo(
		() => [
			{
				id: "gender",
				icon: Users,
				label: t("details.field.gender"),
				value: therapist.gender,
			},
			...(therapist.religion !== undefined
				? [
						{
							id: "religion",
							icon: BookHeart,
							label: t("details.field.religion"),
							value: therapist.religion
								? t(`enums.religion.${therapist.religion}`)
								: t("enums.religion_unset"),
						},
					]
				: []),
			{
				id: "phone",
				icon: Phone,
				label: t("details.field.phone"),
				value: therapist?.phoneNumber
					? formatPhoneNumberIntl(
							therapist.phoneNumber.includes("+")
								? therapist.phoneNumber
								: `+${therapist.phoneNumber}`,
						)
					: "-",
			},
			{
				id: "email",
				icon: Mail,
				label: t("details.field.email"),
				value: therapist.user.email,
			},
			{
				id: "nik",
				icon: IdCard,
				label: t("details.field.nik"),
				value: (
					<NikValue
						key={therapist.id}
						therapistId={therapist.id}
						masked={therapist.nikMasked}
					/>
				),
			},
			{
				id: "telegram",
				icon: AtSignIcon,
				label: t("details.field.telegram_id"),
				value: therapist?.telegramId || t("list.not_available"),
			},
		],
		[therapist, t],
	);

	// employment details
	const employmentDetails = useMemo(
		() => [
			{
				icon: Group,
				label: t("details.field.batch"),
				value: therapist.batch,
			},
			{
				icon: Activity,
				label: t("details.field.status"),
				value: (
					<Badge
						className={cn(
							"text-xs",
							getEmpStatusBadgeVariant(therapist.employmentStatus),
						)}
					>
						{therapist.employmentStatus}
					</Badge>
				),
			},
			{
				icon: Hospital,
				label: t("details.field.therapist_at"),
				value: `${therapist.service.code} - ${therapist.service.name.replaceAll("_", " ")}`,
			},
			{
				icon: BriefcaseMedical,
				label: t("details.field.type"),
				value: therapist.employmentType,
			},
			{
				icon: Microscope,
				label: t("details.field.specializations"),
				value: therapist.specializations?.length ? (
					<div className="flex flex-wrap items-center justify-end gap-1 text-center">
						{therapist.specializations.map((specialization) => (
							<Badge key={specialization}>
								{enumLabel("specialization", specialization)}
							</Badge>
						))}
					</div>
				) : (
					"-"
				),
			},
			{
				icon: Stethoscope,
				label: t("details.field.modalities"),
				value: therapist.modalities?.length ? (
					<div className="flex flex-wrap items-center justify-end gap-1 text-center">
						{therapist.modalities.map((modality) => (
							<Badge key={modality} variant="secondary">
								{enumLabel("modality", modality)}
							</Badge>
						))}
					</div>
				) : (
					"-"
				),
			},
			{
				icon: CalendarDays,
				label: t("details.field.contract_period"),
				value: (() => {
					const start = therapist.contractStartDate
						? format(therapist.contractStartDate, "PP")
						: null;
					const end = therapist.contractEndDate
						? format(therapist.contractEndDate, "PP")
						: null;
					if (start && end) return `${start} — ${end}`;
					if (start) return t("details.contract.start_from", { date: start });
					if (end) return t("details.contract.until", { date: end });
					return t("list.not_available");
				})(),
			},
			{
				icon: CalendarPlus,
				label: t("details.field.joined_at"),
				value: therapist.createdAt
					? format(new Date(therapist.createdAt), `PP, ${timeFormatDateFns}`, {
							locale,
							in: tzDate,
						})
					: t("list.not_available"),
			},
			{
				icon: ClipboardList,
				label: t("details.field.order_count"),
				value: (() => (
					<Badge variant="accent" className="px-1 rounded-full">
						<p className="flex items-center gap-1">
							<ClipboardList className="size-3" />
							<span>
								{t("details.orders", { total: therapist.orderCount || 0 })}
							</span>
						</p>
					</Badge>
				))(),
			},
			{
				icon: Calendar,
				label: t("details.field.visit_count"),
				value: (() => (
					<Badge variant="accent" className="px-1 rounded-full">
						<p className="flex items-center gap-1">
							<Calendar className="size-3" />
							<span>
								{t("details.visits", {
									total: therapist.appointmentCount || 0,
								})}
							</span>
						</p>
					</Badge>
				))(),
			},
		],
		[therapist, locale, tzDate, timeFormatDateFns, t, enumLabel],
	);

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			<Tabs defaultValue="basic" className="w-full">
				<TabsList className="w-full justify-start">
					<TabsTrigger value="basic" className="w-full md:w-auto">
						{t("details.tab.basic")}
					</TabsTrigger>
					<TabsTrigger value="appointments" className="w-full md:w-auto">
						{t("details.tab.appointments")}
					</TabsTrigger>
				</TabsList>

				<TabsContent
					value="basic"
					className="flex flex-col gap-4 max-h-[500px] overflow-auto mt-0"
				>
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
						<div className="flex flex-col gap-4 lg:col-span-6 xl:col-span-5">
							<Card className="h-full shadow-inner">
								<CardHeader>
									<div className="flex items-start w-full gap-2 text-left">
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger>
													<Avatar className="border rounded-lg size-10">
														<AvatarImage src="#" alt={therapist.name} />
														<AvatarFallback
															className={cn(
																"text-sm rounded-lg",
																isSuspended
																	? "bg-destructive text-destructive-foreground"
																	: isAuthCurrentUser
																		? "bg-primary text-primary-foreground"
																		: isOnline
																			? "bg-emerald-700 text-white"
																			: "",
															)}
														>
															{generateInitials(therapist.name)}
														</AvatarFallback>
													</Avatar>
												</TooltipTrigger>
												<TooltipContent>
													{isSuspended ? (
														<div className="flex flex-col">
															{suspendedAt && (
																<span>
																	{t("list.activity.suspend_on")}:{" "}
																	<b>{format(suspendedAt, "PP")}</b>
																</span>
															)}
															<span className="flex items-center">
																{t("list.activity.suspend_until")}:{" "}
																<b>
																	{suspendEnd ? (
																		format(suspendEnd, "PP")
																	) : (
																		<InfinityIcon className="ml-1 size-4" />
																	)}
																</b>
															</span>
														</div>
													) : isAuthSuperAdmin ? (
														<div className="flex flex-col">
															{isOnline ? (
																<span>
																	{t("list.activity.current_ip")}:{" "}
																	<b>{currentIP}</b>
																</span>
															) : (
																<>
																	<span>
																		{t("list.activity.last_ip")}:{" "}
																		<b>{lastIP}</b>
																	</span>
																	{lastOnlineAt && (
																		<span>
																			{t("list.activity.last_online_session")}:{" "}
																			<b>
																				{formatDistanceToNow(lastOnlineAt, {
																					includeSeconds: true,
																					addSuffix: true,
																				})}
																			</b>
																		</span>
																	)}
																</>
															)}
														</div>
													) : (
														<span>
															{isOnline
																? t("list.activity.online")
																: t("list.activity.offline")}
														</span>
													)}
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>

										<div className="flex-1 leading-tight text-left">
											<CardTitle className="text-base uppercase">
												{therapist.name}
											</CardTitle>

											<CardDescription className="text-xs">
												#{therapist.registrationNumber}
											</CardDescription>
										</div>
									</div>
								</CardHeader>

								<CardContent className="grid gap-4">
									{personalDetails.map((p) => (
										<div
											key={p.id}
											className="flex items-start justify-between gap-4 font-light text-pretty"
										>
											<div className="flex items-center gap-2">
												{p.id === "gender" ? (
													getGenderIcon(
														p.value as string,
														"size-4 text-muted-foreground/75 shrink-0",
													)
												) : (
													<p.icon className="size-4 text-muted-foreground/75 shrink-0" />
												)}
												<span>{p.label}: </span>
											</div>

											<div className="font-medium text-right break-words truncate whitespace-normal hyphens-manual">
												{p.value}
											</div>
										</div>
									))}
								</CardContent>
							</Card>
						</div>

						<div className="flex flex-col gap-4 lg:col-span-6 xl:col-span-7">
							<Card className="shadow-inner">
								<CardContent className="p-6">
									<Accordion
										type="single"
										collapsible
										className="w-full"
										defaultValue="employment-details"
									>
										<AccordionItem
											value="employment-details"
											className="border-0"
										>
											<AccordionTrigger className="p-0 uppercase">
												{t("details.section.employment")}
											</AccordionTrigger>
											<AccordionContent className="flex flex-col gap-4 mt-4">
												{employmentDetails.map((p) => (
													<div
														key={p.label}
														className="flex items-start justify-between gap-4 font-light text-pretty"
													>
														<div className="flex items-center gap-2">
															<p.icon className="size-4 text-muted-foreground/75 shrink-0" />
															<span>{p.label}: </span>
														</div>

														<div className="font-medium text-right break-words truncate whitespace-normal hyphens-manual">
															{p.value}
														</div>
													</div>
												))}
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</CardContent>
							</Card>
						</div>
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						<Card className="shadow-inner">
							<CardContent className="p-6">
								<Accordion type="single" collapsible className="w-full">
									<AccordionItem value="account-activity" className="border-0">
										<AccordionTrigger className="p-0 uppercase">
											{t("details.section.account_activity")}
										</AccordionTrigger>
										<AccordionContent className="grid items-center gap-4 pt-6">
											{accounts.items.map((item) => (
												<div
													key={item.id}
													className="flex flex-col font-light text-pretty"
												>
													<span>{item.title}: </span>
													<span className="font-medium break-words truncate whitespace-normal hyphens-manual">
														{item.id === "suspendEnd" && !item.value ? (
															<InfinityIcon className="size-4" />
														) : (
															item.value
														)}
													</span>
												</div>
											))}
										</AccordionContent>
									</AccordionItem>
								</Accordion>
							</CardContent>
						</Card>

						{/* a row of its own: the garis waktu needs the width */}
						<Card className="shadow-inner lg:order-last lg:col-span-2">
							<CardContent className="p-6">
								<Accordion type="single" collapsible className="w-full">
									<AccordionItem value="addresses" className="border-0">
										<AccordionTrigger className="p-0 uppercase">
											{t("details.section.addresses")}
										</AccordionTrigger>
										<AccordionContent className="flex flex-col gap-4 mt-4">
											{!therapist.addresses.length ? (
												<p className="text-sm font-light text-center text-muted-foreground">
													{t("details.address.empty")}
												</p>
											) : (
												<DetailsAddressSection
													addresses={therapist.addresses}
													timeline={addressTimeline}
												/>
											)}
										</AccordionContent>
									</AccordionItem>
								</Accordion>
							</CardContent>
						</Card>

						<Card className="shadow-inner">
							<CardContent className="p-6">
								<Accordion type="single" collapsible className="w-full">
									<AccordionItem value="bank-account" className="border-0">
										<AccordionTrigger className="p-0 uppercase">
											{t("details.section.bank_accounts")}
										</AccordionTrigger>
										<AccordionContent className="flex flex-col gap-4 mt-4">
											{!therapist.bankDetails.length ? (
												<p className="text-sm font-light text-center text-muted-foreground">
													{t("details.bank.empty")}
												</p>
											) : (
												therapist.bankDetails.map((bank, index, array) => (
													<Fragment key={bank.id}>
														<div className="grid gap-2">
															<p className="flex items-center gap-2 font-medium">
																<span>{bank.bankName.toUpperCase()}</span>
																{bank.active && (
																	<Dot
																		className="text-emerald-600"
																		width={10}
																		height={10}
																		strokeWidth={20}
																	/>
																)}
															</p>

															<div className="grid font-light">
																<span>{bank.accountHolderName}</span>
																<span>{bank.accountNumber}</span>
															</div>
														</div>

														{index < array.length - 1 && <Separator />}
													</Fragment>
												))
											)}
										</AccordionContent>
									</AccordionItem>
								</Accordion>
							</CardContent>
						</Card>
					</div>
				</TabsContent>

				<TabsContent
					value="appointments"
					className="gap-4 max-h-[500px] overflow-y-auto mt-0"
				>
					<Deferred
						data={["selectedTherapistAppts"]}
						fallback={<LoadingBasic columnBased />}
					>
						<div className="p-2 md:p-4 py-4 space-y-6">
							{!appts?.data?.length ? (
								<p className="text-sm font-light text-center text-muted-foreground">
									{t("details.appointments.empty")}
								</p>
							) : (
								<>
									{appts.data.map((group) => (
										<div key={group.date} className="flex flex-col gap-2">
											<div className="flex items-center gap-2 mb-1 opacity-75">
												<span className="text-sm font-bold text-foreground text-nowrap">
													{group.date === "Unscheduled"
														? t("details.appointments.unscheduled")
														: format(group.date, "PP")}
												</span>
												<hr className="w-[3rem] border-foreground/25" />
												<span className="text-xs text-muted-foreground italic text-nowrap">
													{t("details.appointments.total_visits", {
														total: group.schedules.length,
													})}
												</span>
												<hr className="w-full border-foreground/25" />
											</div>

											<div className="grid grid-cols-1 lg:grid-cols-2">
												{group.schedules.map((appt) => (
													<Suspense
														key={appt.id}
														fallback={<LoadingBasic columnBased={true} />}
													>
														<ApptCard appt={appt} hideDate />
													</Suspense>
												))}
											</div>
										</div>
									))}

									{appts.metadata && (
										<ApptPagination
											metadata={appts.metadata}
											actions={{
												goToPrevpage: () =>
													onPageChange(
														appts.metadata.page - 1,
														appts.metadata.limit,
													),
												goToNextPage: () =>
													onPageChange(
														appts.metadata.page + 1,
														appts.metadata.limit,
													),
												goToPage: (value) =>
													onPageChange(Number(value), appts.metadata.limit),
												goToFirstPage: () =>
													onPageChange(1, appts.metadata.limit),
												goToLastPage: () =>
													onPageChange(
														appts.metadata.pages,
														appts.metadata.limit,
													),
												onChangeLimit: (value) =>
													onPageChange(1, Number(value)),
											}}
										/>
									)}
								</>
							)}
						</div>
					</Deferred>
				</TabsContent>
			</Tabs>

			{isDekstop && (
				<DialogFooter>
					<DialogClose asChild>
						<Button type="button" variant="ghost">
							{i18n.t("components.modal.close")}
						</Button>
					</DialogClose>
				</DialogFooter>
			)}
		</div>
	);
}
